import React, { useState } from 'react';
import { Job } from "@/components/jobs/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Calendar, 
  DollarSign, 
  MapPin, 
  Users, 
  Clock,
  Target,
  Building,
  Mail,
  Phone,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';
import { TargetHireDateDialog } from './TargetHireDateDialog';
import { MessageDialog } from './MessageDialog';

interface JobCriticalFactsSidebarProps {
  job: Job;
}

export const JobCriticalFactsSidebar: React.FC<JobCriticalFactsSidebarProps> = ({ job }) => {
  const [showTargetDateDialog, setShowTargetDateDialog] = useState(false);
  const [showHiringManagerDialog, setShowHiringManagerDialog] = useState(false);

  // Mock data for demonstration
  const criticalData = {
    postingStatus: {
      indeed: { status: 'active', applications: 12 },
      linkedin: { status: 'active', applications: 8 },
      glassdoor: { status: 'pending', applications: 0 },
      companyWebsite: { status: 'active', applications: 4 }
    },
    budget: {
      placementFee: 25000,
      estimatedRevenue: 22500
    },
    dates: {
      posted: job.created_at,
      targetHire: '2024-02-15',
      deadline: '2024-02-28'
    },
    team: {
      recruiter: { name: 'John Smith', email: 'john@company.com' },
      hiringManager: { 
        name: 'Sarah Johnson', 
        email: 'sarah@client.com', 
        phone: '+1-555-0123',
        id: 'hiring-manager-1'
      }
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'paused': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
      default: return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    }
  };

  const handleContactHiringManager = () => {
    setShowHiringManagerDialog(true);
  };

  return (
    <>
      <div className="space-y-4">
        {/* Posting Status */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ExternalLink className="h-4 w-4" />
              Posting Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(criticalData.postingStatus).map(([platform, data]) => (
              <div key={platform} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className={getStatusColor(data.status)}>
                    {data.status}
                  </Badge>
                  <span className="text-sm capitalize">{platform.replace(/([A-Z])/g, ' $1')}</span>
                </div>
                <span className="text-xs text-muted-foreground">{data.applications} apps</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Budget Information */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Budget & Revenue
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Placement Fee:</span>
                <span className="font-medium">${criticalData.budget.placementFee.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Est. Revenue:</span>
                <span className="font-medium text-green-600">${criticalData.budget.estimatedRevenue.toLocaleString()}</span>
              </div>
              {(job.salary_min || job.salary_max) && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Salary Range:</span>
                  <span className="font-medium">
                    {job.salary_min && job.salary_max 
                      ? `$${job.salary_min.toLocaleString()} - $${job.salary_max.toLocaleString()}`
                      : job.salary_min 
                        ? `From $${job.salary_min.toLocaleString()}`
                        : `Up to $${job.salary_max?.toLocaleString()}`
                    }
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Key Dates */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Key Dates
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Posted:</span>
                <span className="font-medium">{formatDate(criticalData.dates.posted)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Target Hire:</span>
                <span className="font-medium">{formatDate(criticalData.dates.targetHire)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Deadline:</span>
                <div className="flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3 text-amber-500" />
                  <span className="font-medium">{formatDate(criticalData.dates.deadline)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team & Contacts */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-4 w-4" />
              Team & Contacts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium text-sm mb-2">Assigned Recruiter</h4>
              <div className="text-sm">
                <div className="font-medium">{criticalData.team.recruiter.name}</div>
                <div className="text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {criticalData.team.recruiter.email}
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-sm mb-2">Hiring Manager</h4>
              <div className="text-sm space-y-1">
                <div className="font-medium">{criticalData.team.hiringManager.name}</div>
                <div className="text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {criticalData.team.hiringManager.email}
                </div>
                <div className="text-muted-foreground flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {criticalData.team.hiringManager.phone}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              size="sm" 
              className="w-full justify-start"
              onClick={() => setShowTargetDateDialog(true)}
            >
              <Target className="h-3 w-3 mr-2" />
              Set Target Hire Date
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="w-full justify-start"
              onClick={handleContactHiringManager}
            >
              <Building className="h-3 w-3 mr-2" />
              Contact Hiring Manager
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => setShowTargetDateDialog(true)}
            >
              <Clock className="h-3 w-3 mr-2" />
              Extend Deadline
            </Button>
          </CardContent>
        </Card>
      </div>

      <TargetHireDateDialog
        isOpen={showTargetDateDialog}
        onClose={() => setShowTargetDateDialog(false)}
        jobId={job.id}
      />

      {criticalData.team.hiringManager && (
        <MessageDialog
          isOpen={showHiringManagerDialog}
          onClose={() => setShowHiringManagerDialog(false)}
          candidateId={criticalData.team.hiringManager.id}
          candidateName={criticalData.team.hiringManager.name}
          candidateEmail={criticalData.team.hiringManager.email}
        />
      )}
    </>
  );
};
